import { type SearchResult, useApiV1Omnisearch } from 'api';
import { useTranslation } from 'i18n';
import { useState } from 'react';
import { Autocomplete, styled, useBreakpoint } from 'ui';
import { Search } from 'ui/icons';

import { useDebounce } from '../../../debounce';

const OptionLink = styled('a')({
  color: 'inherit',
  textDecoration: 'none',
});

type OmnisearchAutocompleteProps = {
  onOptionSelect: () => void;
  autocompleteRef: React.RefObject<HTMLDivElement | null>;
};

const DEBOUNCE_DELAY = 300;

export const OmnisearchAutocomplete = ({ onOptionSelect, autocompleteRef }: OmnisearchAutocompleteProps) => {
  const { isMobile } = useBreakpoint();
  const { t } = useTranslation('features');
  const [value, setValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');

  const debouncedSetValue = useDebounce((newValue: string) => {
    setDebouncedValue(newValue);
  }, DEBOUNCE_DELAY);

  const { data, isLoading } = useApiV1Omnisearch(
    {
      q: debouncedValue,
    },
    {
      query: {
        enabled: debouncedValue.length > 1,
      },
    }
  );

  const options = data?.results || [];

  const handleChange = (_event: React.SyntheticEvent, newValue: SearchResult | null): void => {
    if (!newValue) {
      return;
    }
    onOptionSelect();

    window.open(newValue.url, '_self');
  };

  return (
    <Autocomplete<SearchResult>
      ref={autocompleteRef}
      inputValue={value}
      fullWidth
      autoFocus={true}
      autoSelect
      size={isMobile ? 'medium' : 'small'}
      autoHighlight
      loading={isLoading}
      placeholder={isMobile ? t('omnisearch.placeholder.mobile') : t('omnisearch.placeholder.desktop')}
      noOptionsText={value ? t('omnisearch.noOptions.withValue') : t('omnisearch.noOptions.withoutValue')}
      startAdornment={<Search />}
      options={options}
      onInputChange={(_event, newValue) => {
        setValue(newValue);
        debouncedSetValue(newValue);
      }}
      onChange={handleChange}
      getOptionLabel={(option) => option.title || ''}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <OptionLink href={option.url} key={key}>
            <li {...optionProps}>
              {option.icon} {option.title}
            </li>
          </OptionLink>
        );
      }}
      isOptionEqualToValue={(option, value) => value === undefined || option?.title === (value?.title ?? value)}
      sx={{
        minWidth: isMobile ? '100%' : 260,
      }}
    />
  );
};
